import { Size } from '@ps/web-core-enums';
import { Translation, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CSSTransition } from 'react-transition-group';

import WebpWithFallback from '@swf/components/WebpWithFallback/WebpWithFallback';
import { useIsMobileDevice } from '@swf/poker';

import {
  TXTCLI_Disable_Video_Chats,
  TXTCLI_Enable_Video_Chats,
  TXTCLI_OFF,
  TXTCLI_ON,
  TXTCLI_Video_Chat,
  TXTWCL_Video_chat_is_currently_suppor_ELL
} from './VideoChatToggle.messages.json';
import {
  container,
  containerHover,
  disabled,
  displayAria,
  imageEnter,
  imageEnterActive,
  imageExit,
  imageExitActive,
  imagesContainer,
  imgContainer,
  label,
  labelOn,
  visible
} from './VideoChatToggle.scss';
import videoChatOffLongPNG from './assets/video-chat-off-long.png';
import videoChatOffLong from './assets/video-chat-off-long.webp';
import videoChatOffPNG from './assets/video-chat-off.png';
import videoChatOff from './assets/video-chat-off.webp';
import videoChatOnLongPNG from './assets/video-chat-on-long.png';
import videoChatOnLong from './assets/video-chat-on-long.webp';
import videoChatOnPNG from './assets/video-chat-on.png';
import videoChatOn from './assets/video-chat-on.webp';

export interface Props {
  checked: boolean;
  supported: boolean;
  onChange: () => void;
  className?: string;
  imageContainerClassName?: string;
}

export const width = 182;
export const height = 127;

export const dataTestId = 'video-chat-toggle-v1';

export const transition = {
  enter: imageEnter,
  enterActive: imageEnterActive,
  exit: imageExit,
  exitActive: imageExitActive
};

const VideoChatToggle = ({
  className = '',
  imageContainerClassName = '',
  checked,
  supported,
  onChange
}: Props) => {
  const { formatMessage } = useIntl();
  const { greaterThan } = useScreenSize();
  const labelText = checked ? TXTCLI_ON : TXTCLI_OFF;
  const hoverLabel = checked
    ? formatMessage(TXTCLI_Disable_Video_Chats)
    : formatMessage(TXTCLI_Enable_Video_Chats);
  const unsupportedLabel = formatMessage(TXTWCL_Video_chat_is_currently_suppor_ELL, { 0: '6 & 9' });
  const handleClick = useCallback(() => {
    if (!supported) return;

    onChange();
  }, [onChange, supported]);

  const isMobileDevice = useIsMobileDevice(true);

  const shouldDisplayAria = !greaterThan(Size.Medium) && !supported;

  return (
    <div className={className}>
      <label>
        <Translation {...TXTCLI_Video_Chat} />
      </label>
      <div
        data-testid={dataTestId}
        className={classNames(container, {
          [disabled]: !supported,
          [containerHover]: !isMobileDevice,
          [displayAria]: shouldDisplayAria
        })}
        onClick={handleClick}
        aria-disabled={!supported}
        aria-label={supported ? hoverLabel : unsupportedLabel}
      >
        <div className={classNames(imagesContainer, imageContainerClassName)}>
          <CSSTransition in={!checked} classNames={transition} timeout={200} unmountOnExit>
            <WebpWithFallback
              src={greaterThan(Size.Medium) ? videoChatOff : videoChatOffLong}
              srcFallback={greaterThan(Size.Medium) ? videoChatOffPNG : videoChatOffLongPNG}
              containerClassName={classNames(imgContainer, { [visible]: !checked })}
              imageSize={{ height, width }}
            />
          </CSSTransition>
          <CSSTransition in={checked} classNames={transition} timeout={200} unmountOnExit>
            <WebpWithFallback
              src={greaterThan(Size.Medium) ? videoChatOn : videoChatOnLong}
              srcFallback={greaterThan(Size.Medium) ? videoChatOnPNG : videoChatOnLongPNG}
              containerClassName={classNames(imgContainer, { [visible]: checked })}
              imageSize={{ height, width }}
            />
          </CSSTransition>
        </div>
        <div className={classNames(label, { [labelOn]: checked })}>
          <Translation {...labelText} />
        </div>
      </div>
    </div>
  );
};
export default VideoChatToggle;
